/**
 * Main App Component
 * Router setup and layout
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Vehicles from './pages/Vehicles';
import VehicleDetail from './pages/VehicleDetail';
import VehicleForm from './pages/VehicleForm';
import Purchases from './pages/Purchases';
import PurchaseDetail from './pages/PurchaseDetail';
import PurchaseForm from './pages/PurchaseForm';
import Investors from './pages/Investors';
import Bookings from './pages/Bookings';
import BookingDetail from './pages/BookingDetail';
import BookingForm from './pages/BookingForm';
import BookingCalendar from './pages/BookingCalendar';
import BookingDashboard from './pages/BookingDashboard';
import Trips from './pages/Trips';
import TripDetail from './pages/TripDetail';
import TripRequests from './pages/TripRequests';
import TripRequestForm from './pages/TripRequestForm';
import TripRequestDetail from './pages/TripRequestDetail';
import Dispatches from './pages/Dispatches';
import DispatchDetail from './pages/DispatchDetail';
import Locations from './pages/Locations';
import LocationForm from './pages/LocationForm';
import LocationDetail from './pages/LocationDetail';
import CorporatePriceLists from './pages/CorporatePriceLists';
import CorporatePriceListForm from './pages/CorporatePriceListForm';
import CorporatePriceListDetail from './pages/CorporatePriceListDetail';
import RoutePrices from './pages/RoutePrices';
import RoutePriceForm from './pages/RoutePriceForm';
import RoutePriceDetail from './pages/RoutePriceDetail';
import CostCenterMappings from './pages/CostCenterMappings';
import CostCenterMappingForm from './pages/CostCenterMappingForm';
import CostCenterMappingDetail from './pages/CostCenterMappingDetail';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/admin/login" element={<Login />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vehicles"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Vehicles />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vehicles/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VehicleForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vehicles/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VehicleDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vehicles/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VehicleForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/purchases"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Purchases />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/purchases/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/purchases/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/purchases/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/investors"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Investors />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Bookings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings/calendar"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingCalendar />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trips"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Trips />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trips/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TripDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trip-requests"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TripRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trip-requests/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TripRequestForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trip-requests/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TripRequestForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trip-requests/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TripRequestDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dispatches"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dispatches />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dispatches/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DispatchDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Locations Routes */}
            <Route
              path="/admin/locations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Locations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LocationForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LocationDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LocationForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Corporate Price Lists Routes */}
            <Route
              path="/admin/corporate-price-lists"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CorporatePriceLists />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/corporate-price-lists/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CorporatePriceListForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/corporate-price-lists/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CorporatePriceListDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/corporate-price-lists/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CorporatePriceListForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Route Prices Routes */}
            <Route
              path="/admin/route-prices"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RoutePrices />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/route-prices/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RoutePriceForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/route-prices/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RoutePriceDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/route-prices/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RoutePriceForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Cost Center Mappings Routes */}
            <Route
              path="/admin/cost-center-mappings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CostCenterMappings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cost-center-mappings/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CostCenterMappingForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cost-center-mappings/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CostCenterMappingDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cost-center-mappings/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CostCenterMappingForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            {/* Catch-all. Without one, any path React Router does not match
                renders an empty document with nothing in the console - which is
                how a missing detail route in the portal presented as a blank
                page rather than an error. Every admin navigation target
                currently resolves; this keeps a future gap visible. */}
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

